import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { User } from '@shared/interfaces/user.interface';
import { HomesService } from '@shared/services/homes.service';
import { HomeDto } from '@shared/interfaces/dtos/home-dto.interface';
import { INotification, TranslateService } from '@devinforius/i-essentials';
import { MenuItem, PrimeNGConfig } from 'primeng/api';
import { Notifier } from '@core/services/notifier.service';
import { PagesEndpoints } from '@shared/enums/pages-endpoints.enum';
import { INavbarConfig } from '@devinforius/i-essentials/lib/interfaces';
import { environment } from './../environments/environment.dev';
import { configSSO } from '../environments/sso/auth.clientConfiguration.dev';
import { HttpClient } from '@angular/common/http';
import { FormOptionsService } from '@shared/services/form-options.service';
import { isEmpty } from 'lodash';
import { differenceInMinutes } from 'date-fns';
import { YearsService } from '@shared/services/years.service';

@Component({
  selector: 'im-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly translater = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly homesService = inject(HomesService);
  private readonly formOptionsService = inject(FormOptionsService);
  private readonly notifier = inject(Notifier);
  private readonly primeNGConfig = inject(PrimeNGConfig);
  public readonly authService = inject(AuthService);
  public readonly yearsService = inject(YearsService);

  public isConnected: boolean = false;
  public title = 'IMandat';
  public navbarConfig = signal<INavbarConfig>({} as INavbarConfig);
  public currentHome = signal<HomeDto | null>(null);
  public currentHomeName = computed<string>(
    () => this.currentHome()?.label ?? '',
  );
  public user = signal<User | null>(null);
  public notifications: INotification[] = [];
  public environmentTitle: string = environment.environmentTitle;
  public isAPIAvailable = signal<boolean>(true);
  public readonly yearsHaveBeenLoaded = signal<boolean>(true);

  ngOnInit() {
    this.initApp();
    this.checkAPIHealth();
  }

  private async initApp(): Promise<void> {
    await this.translater.init(
      'fr',
      ['fr'],
      ['assets/translations', 'assets/i18n'],
    );

    this.authService.isConnected$.subscribe({
      next: (connected) => {
        if (connected) {
          this.initSideBarProperties();
          this.initGlobalFormOptions();
          this.initYears();
          this.checkIfUpdateIsComingSoon();
          this.yearsHaveBeenLoaded.set(false);
        }
      },
    });

    this.translater.onTranslationReady.subscribe(() => {
      this.initPrimeNGTranslations();
    });
    this.notifier.setKey('imandat');
  }

  private initYears(): void {
    this.yearsHaveBeenLoaded.set(false);

    this.yearsService.init().then(() => {
      this.yearsHaveBeenLoaded.set(true);
    });
  }

  public get isNavBarFullySet(): boolean {
    return !isEmpty(this.navbarConfig());
  }

  private initGlobalFormOptions(): void {
    this.formOptionsService.init();
  }

  private checkAPIHealth(): void {
    const healthCheckUrl = `${
      environment.baseUrl.split('/api/v1')[0]
    }/liveness`;

    this.http.get(healthCheckUrl).subscribe({
      next: () => {
        this.isAPIAvailable.set(true);
      },
      error: () => {
        this.isAPIAvailable.set(false);
      },
    });
  }

  private checkIfUpdateIsComingSoon(): void {
    if (!environment.production) return;

    const nextUpcomingUpdateDate = environment.nextPlannedUpdate;

    if (nextUpcomingUpdateDate) {
      const diff = differenceInMinutes(nextUpcomingUpdateDate, new Date());

      if (diff <= 30 && diff >= 0) {
        this.showUserForNextUpdate(diff);

        const interval = setInterval(() => {
          const diff = differenceInMinutes(nextUpcomingUpdateDate, new Date());

          if (diff === 0) {
            this.authService.logout();
            clearInterval(interval);
          }
        }, 1000);
      }
    }
  }

  private showUserForNextUpdate(timeleft: number): void {
    this.notifier.show(
      'warn',
      'Attention',
      `Une mise à jour va avoir lieu dans moins de ${timeleft} minutes. L'application sera indisponible pendant cette période.`,
      10000,
    );
  }

  private initPrimeNGTranslations(): void {
    this.primeNGConfig.setTranslation({
      startsWith: this.translater.instant('PRIMENG.STARTSWITH'),
      contains: this.translater.instant('PRIMENG.CONTAINS'),
      notContains: this.translater.instant('PRIMENG.NOTCONTAINS'),
      endsWith: this.translater.instant('PRIMENG.ENDSWITH'),
      equals: this.translater.instant('PRIMENG.EQUALS'),
      notEquals: this.translater.instant('PRIMENG.NOTEQUALS'),
      noFilter: this.translater.instant('PRIMENG.NOFILTER'),
      lt: this.translater.instant('PRIMENG.LT'),
      lte: this.translater.instant('PRIMENG.LTE'),
      gt: this.translater.instant('PRIMENG.GT'),
      gte: this.translater.instant('PRIMENG.GTE'),
      is: this.translater.instant('PRIMENG.IS'),
      isNot: this.translater.instant('PRIMENG.ISNOT'),
      before: this.translater.instant('PRIMENG.BEFORE'),
      after: this.translater.instant('PRIMENG.AFTER'),
      dateIs: this.translater.instant('PRIMENG.DATEIS'),
      dateIsNot: this.translater.instant('PRIMENG.DATEISNOT'),
      dateBefore: this.translater.instant('PRIMENG.DATEBEFORE'),
      dateAfter: this.translater.instant('PRIMENG.DATEAFTER'),
      clear: this.translater.instant('PRIMENG.CLEAR'),
      apply: this.translater.instant('PRIMENG.APPLY'),
      matchAll: this.translater.instant('PRIMENG.MATCHALL'),
      matchAny: this.translater.instant('PRIMENG.MATCHANY'),
      addRule: this.translater.instant('PRIMENG.ADDRULE'),
      removeRule: this.translater.instant('PRIMENG.REMOVERULE'),
      accept: this.translater.instant('PRIMENG.ACCEPT'),
      reject: this.translater.instant('PRIMENG.REJECT'),
      choose: this.translater.instant('PRIMENG.CHOOSE'),
      upload: this.translater.instant('PRIMENG.UPLOAD'),
      cancel: this.translater.instant('PRIMENG.CANCEL'),
      dayNames: [
        this.translater.instant('PRIMENG.DAYNAMES.SUNDAY'),
        this.translater.instant('PRIMENG.DAYNAMES.MONDAY'),
        this.translater.instant('PRIMENG.DAYNAMES.TUESDAY'),
        this.translater.instant('PRIMENG.DAYNAMES.WEDNESDAY'),
        this.translater.instant('PRIMENG.DAYNAMES.THURSDAY'),
        this.translater.instant('PRIMENG.DAYNAMES.FRIDAY'),
        this.translater.instant('PRIMENG.DAYNAMES.SATURDAY'),
      ],
      dayNamesShort: [
        this.translater.instant('PRIMENG.DAYNAMESSHORT.SUNDAY'),
        this.translater.instant('PRIMENG.DAYNAMESSHORT.MONDAY'),
        this.translater.instant('PRIMENG.DAYNAMESSHORT.TUESDAY'),
        this.translater.instant('PRIMENG.DAYNAMESSHORT.WEDNESDAY'),
        this.translater.instant('PRIMENG.DAYNAMESSHORT.THURSDAY'),
        this.translater.instant('PRIMENG.DAYNAMESSHORT.FRIDAY'),
        this.translater.instant('PRIMENG.DAYNAMESSHORT.SATURDAY'),
      ],
      dayNamesMin: [
        this.translater.instant('PRIMENG.DAYNAMESMIN.SUNDAY'),
        this.translater.instant('PRIMENG.DAYNAMESMIN.MONDAY'),
        this.translater.instant('PRIMENG.DAYNAMESMIN.TUESDAY'),
        this.translater.instant('PRIMENG.DAYNAMESMIN.WEDNESDAY'),
        this.translater.instant('PRIMENG.DAYNAMESMIN.THURSDAY'),
        this.translater.instant('PRIMENG.DAYNAMESMIN.FRIDAY'),
        this.translater.instant('PRIMENG.DAYNAMESMIN.SATURDAY'),
      ],
      monthNames: [
        this.translater.instant('PRIMENG.MONTHNAMES.JANUARY'),
        this.translater.instant('PRIMENG.MONTHNAMES.FEBRUARY'),
        this.translater.instant('PRIMENG.MONTHNAMES.MARCH'),
        this.translater.instant('PRIMENG.MONTHNAMES.APRIL'),
        this.translater.instant('PRIMENG.MONTHNAMES.MAY'),
        this.translater.instant('PRIMENG.MONTHNAMES.JUNE'),
        this.translater.instant('PRIMENG.MONTHNAMES.JULY'),
        this.translater.instant('PRIMENG.MONTHNAMES.AUGUST'),
        this.translater.instant('PRIMENG.MONTHNAMES.SEPTEMBER'),
        this.translater.instant('PRIMENG.MONTHNAMES.OCTOBER'),
        this.translater.instant('PRIMENG.MONTHNAMES.NOVEMBER'),
        this.translater.instant('PRIMENG.MONTHNAMES.DECEMBER'),
      ],
      monthNamesShort: [
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.JANUARY'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.FEBRUARY'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.MARCH'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.APRIL'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.MAY'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.JUNE'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.JULY'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.AUGUST'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.SEPTEMBER'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.OCTOBER'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.NOVEMBER'),
        this.translater.instant('PRIMENG.MONTHNAMESSHORT.DECEMBER'),
      ],
      dateFormat: this.translater.instant('PRIMENG.DATEFORMAT'),
      firstDayOfWeek: 1,
      today: this.translater.instant('PRIMENG.TODAY'),
      weekHeader: this.translater.instant('PRIMENG.WEEKHEADER'),
      weak: this.translater.instant('PRIMENG.WEAK'),
      medium: this.translater.instant('PRIMENG.MEDIUM'),
      strong: this.translater.instant('PRIMENG.STRONG'),
      passwordPrompt: this.translater.instant('PRIMENG.PASSWORDPROMPT'),
      emptyMessage: this.translater.instant('PRIMENG.EMPTYMESSAGE'),
      emptyFilterMessage: this.translater.instant('PRIMENG.EMPTYFILTERMESSAGE'),
    });
  }

  private setConnectionState(): void {
    this.authService.isConnected$.subscribe({
      next: (isConnected: boolean) => (this.isConnected = isConnected),
    });
  }

  private setCurrentHome(): void {
    this.homesService.currentVisitedHome$.subscribe({
      next: (home: HomeDto | null) => {
        this.currentHome.set(home);
      },
    });
  }

  private setUser(): void {
    this.authService.user$.subscribe({
      next: (user: User | null) => this.user.set(user),
    });
  }

  private initSideBarProperties(): void {
    this.setSideBarLinks();
    this.setConnectionState();
    this.setCurrentHome();
    this.setUser();
  }

  private setSideBarLinks(): void {
    const navlinks = [
      {
        title: this.translater.instant('GLOBAL.SIDE_BAR.NAV_LINKS.SCANS.TITLE'),
        label: this.translater.instant('GLOBAL.SIDE_BAR.NAV_LINKS.SCANS.LABEL'),
        img: 'assets/side-bar/scanner.svg',
        click: () =>
          this.router.navigateByUrl(
            `private/${this.currentHome()?.name}/${PagesEndpoints.Scans}`,
          ),
      },
      {
        title: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.MANDATES_RECOVERIES.TITLE',
        ),
        label: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.MANDATES_RECOVERIES.LABEL',
        ),
        img: 'assets/side-bar/mandate.svg',
        click: () =>
          this.router.navigateByUrl(
            `private/${this.currentHome()?.name}/${PagesEndpoints.Mandates}`,
          ),
      },
      {
        title: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.SIGNATURE_BUNDLES.TITLE',
        ),
        label: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.SIGNATURE_BUNDLES.LABEL',
        ),
        img: 'assets/side-bar/bundle.svg',
        click: () =>
          this.router.navigateByUrl(
            `private/${this.currentHome()?.name}/signature/${
              PagesEndpoints.Bundles
            }`,
          ),
      },
      {
        title: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.CONTROL.TITLE',
        ),
        label: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.CONTROL.LABEL',
        ),
        img: 'assets/side-bar/control.svg',
        click: () =>
          this.router.navigateByUrl(
            `private/${this.currentHome()?.name}/${PagesEndpoints.Control}`,
          ),
      },
      {
        title: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.CLASSIFICATION.TITLE',
        ),
        label: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.CLASSIFICATION.LABEL',
        ),
        img: 'assets/side-bar/classifying.svg',
        click: () =>
          this.router.navigateByUrl(
            `private/${this.currentHome()?.name}/${
              PagesEndpoints.Classification
            }`,
          ),
      },
      {
        title: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.ADMINISTRATION.TITLE',
        ),
        label: this.translater.instant(
          'GLOBAL.SIDE_BAR.NAV_LINKS.ADMINISTRATION.LABEL',
        ),
        img: 'assets/side-bar/admin.svg',
        click: (): void => {
          this.router.navigateByUrl(
            `admin/${this.currentHome()?.name}/${PagesEndpoints.Admin}/global`,
          );
        },
        children: [
          {
            title: this.translater.instant(
              'GLOBAL.SIDE_BAR.NAV_LINKS.ADMINISTRATION.CHILDREN.GLOBAL.TITLE',
            ),
            label: this.translater.instant(
              'GLOBAL.SIDE_BAR.NAV_LINKS.ADMINISTRATION.CHILDREN.GLOBAL.LABEL',
            ),
            icon: 'bi bi-gear-wide-connected',
            click: () =>
              this.router.navigateByUrl(
                `admin/${this.currentHome()?.name}/${
                  PagesEndpoints.Admin
                }/global`,
              ),
          },
          {
            title: this.translater.instant(
              'GLOBAL.SIDE_BAR.NAV_LINKS.ADMINISTRATION.CHILDREN.USERS.TITLE',
            ),
            label: this.translater.instant(
              'GLOBAL.SIDE_BAR.NAV_LINKS.ADMINISTRATION.CHILDREN.USERS.LABEL',
            ),
            icon: 'bi bi-people-fill',
            click: () => {
              this.router.navigateByUrl(
                `admin/${this.currentHome()?.name}/${
                  PagesEndpoints.Admin
                }/users`,
              );
            },
          },
          {
            title: this.translater.instant(
              'GLOBAL.SIDE_BAR.NAV_LINKS.ADMINISTRATION.CHILDREN.PROFILES.TITLE',
            ),
            label: this.translater.instant(
              'GLOBAL.SIDE_BAR.NAV_LINKS.ADMINISTRATION.CHILDREN.PROFILES.LABEL',
            ),
            icon: 'bi bi-person-badge-fill',
            click: () => {
              this.router.navigateByUrl(
                `admin/${this.currentHome()?.name}/${
                  PagesEndpoints.Admin
                }/profiles`,
              );
            },
          },
        ],
      },
    ];
    this.authService.fetchUserInfos().subscribe({
      next: (userInfos) => {
        this.navbarConfig.set({
          names: {
            app: 'iMandat',
            home: userInfos.home.label,
            user: `${userInfos.firstname} ${userInfos.lastname}`,
            env: !environment.production
              ? environment.environmentTitle
              : undefined,
          },
          links: {
            navLinks: [...navlinks],
          },
          routes: {
            assistance: 'public/support',
          },
          showButtons: {
            admin: true,
            languages: true,
          },
          menus: {
            langMenuItems: [
              { label: 'Français', command: () => this.onLanguageChange('fr') },
              {
                label: 'Nederlands',
                command: () => this.onLanguageChange('nl'),
              },
              { label: 'English', command: () => this.onLanguageChange('en') },
              { label: 'Deutsch', command: () => this.onLanguageChange('de') },
            ],
            userMenuItems: [
              {
                label: this.translater.instant(
                  'GLOBAL.SIDE_BAR.SETTINGS.ACCESS_DELEGATION',
                ),
                icon: 'bi bi-gear-wide-connected',
                command: () => {
                  window.open(
                    `${configSSO.issuer}/Support/DelegationRequests?clientid=${configSSO.clientId}&clientUri=${environment.clientUri}`,
                    '_blank',
                  );
                },
              },
            ],
          },
          isSmallSidebar: false,
          appColor: getComputedStyle(document.documentElement).getPropertyValue(
            '--primary-color',
          ),
        });

        if (environment.devMode) {
          this.navbarConfig.update((previousValue) => ({
            ...previousValue,
            userMenuItems: [
              ...(this.navbarConfig().menus.userMenuItems as MenuItem[]),
              {
                label: 'DEV',
                icon: 'bi bi-display',
                command: () => {
                  this.router.navigate(['/dev']);
                },
              },
            ],
          }));
        }

        if (userInfos.isSuperAdmin) {
          this.navbarConfig.update((previousValue) => ({
            ...previousValue,
            links: {
              ...previousValue.links,
              navLinks: [
                ...previousValue.links.navLinks,
                {
                  title: 'Dashboard',
                  label: 'Dashboard',
                  img: 'assets/side-bar/dashboard.png',
                  click: (): void => {
                    this.router.navigateByUrl(
                      `admin/${this.currentHome()?.name}/${
                        PagesEndpoints.Admin
                      }/dashboard`,
                    );
                  },
                },
              ],
            },
          }));
        }

        if (!this.authService.canUseISign()) {
          const bundleLinkLabel = this.translater.instant(
            'GLOBAL.SIDE_BAR.NAV_LINKS.SIGNATURE_BUNDLES.LABEL',
          );

          this.navbarConfig.update((previousValue) => ({
            ...previousValue,
            links: {
              ...previousValue.links,
              navLinks: previousValue.links.navLinks.filter(
                (link) => link.label !== bundleLinkLabel,
              ),
            },
          }));
        }
      },
    });
  }

  public onLanguageChange(lan: string): void {
    if (lan !== this.translater.getCurrentLang()) {
      this.translater.setCurrentLang(lan).then(() => this.setSideBarLinks());
    }
  }
}
